const AUTH_STORAGE_KEYS = [
  "user",
  "sessionId",
  "journey",
  "Permission",

  // Metabase/web embed tokens & dashboards
  "financeToken",
  "vccToken",
  "operationsToken",
  "searchToken",
  "walletToken",
  "buyerIdToken",
  "managementReports",
  "exportReports",
  "shippingReports",
  "finance2Report",

  // Named report tokens
  "muhammadHusniReport",
  "anassFahmouneReport",
  "awsALiJrdyReport",
  "koulMabroukReport",
  "rehmatUllahKhanReport",
  "ravinAbdulkareemReport",
  "shohratAshyrovReport",
  "ardamehrShoevReport",
  "rafiMahmoodReport",
  "riazMuhammadReport",
  "saqibUmerReport",
  "BuyerIdAnalysisReport",
  "AgentBotReport",
  "ClaimVehicleMobileReport",
  "TTAnalysisReport",
  "rashidKpisDashboard",
  "allMarketingTeamReport",
  "NawarYaseerHlebeahReport",
];

const WEB_TOKEN_KEYS = new Set([
  // Metabase/web embed tokens & dashboards
  "financeToken",
  "vccToken",
  "operationsToken",
  "searchToken",
  "walletToken",
  "buyerIdToken",
  "managementReports",
  "exportReports",
  "shippingReports",
  "finance2Report",

  // Named report tokens
  "muhammadHusniReport",
  "anassFahmouneReport",
  "awsALiJrdyReport",
  "koulMabroukReport",
  "rehmatUllahKhanReport",
  "ravinAbdulkareemReport",
  "shohratAshyrovReport",
  "ardamehrShoevReport",
  "rafiMahmoodReport",
  "riazMuhammadReport",
  "saqibUmerReport",
  "BuyerIdAnalysisReport",
  "AgentBotReport",
  "ClaimVehicleMobileReport",
  "TTAnalysisReport",
  "rashidKpisDashboard",
  "allMarketingTeamReport",
  "NawarYaseerHlebeahReport",
]);

const AUTH_SYNC_CHANNEL_NAME = "authStorage:sync";
const AUTH_SYNC_KEYS = ["user", "sessionId", "Permission", "journey"];

function getAuthSyncChannel() {
  if (typeof window === "undefined") return null;
  if (typeof window.BroadcastChannel === "undefined") return null;
  try {
    return new BroadcastChannel(AUTH_SYNC_CHANNEL_NAME);
  } catch {
    return null;
  }
}

// Respond to sync requests from other tabs.
const __authSyncChannel = getAuthSyncChannel();
if (__authSyncChannel) {
  __authSyncChannel.onmessage = (event) => {
    const msg = event?.data;
    if (!msg || msg.type !== "AUTH_SYNC_REQUEST") return;

    const requestId = msg.requestId;
    const keys = Array.isArray(msg.keys) ? msg.keys : AUTH_SYNC_KEYS;

    const primary = getPrimaryStorage();
    const legacy = getLegacyStorage();

    const items = {};
    for (const key of keys) {
      // Never share embed tokens across tabs.
      if (WEB_TOKEN_KEYS.has(key)) continue;

      const rawPrimary = safeGet(primary, key);
      const rawLegacy = safeGet(legacy, key);
      const value = rawPrimary != null ? rawPrimary : rawLegacy;
      if (value != null) items[key] = value;
    }

    try {
      __authSyncChannel.postMessage({
        type: "AUTH_SYNC_RESPONSE",
        requestId,
        items,
      });
    } catch {
      // ignore
    }
  };
}

const authMemoryCache = new Map();

function emitAuthStorageChanged(key) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent("authStorage:changed", { detail: { key } }));
  } catch {
    // ignore
  }
}

function getPrimaryStorage() {
  if (typeof window === "undefined") return null;
  // Use localStorage so authentication survives opening a new tab/window.
  // sessionStorage is per-tab and causes fresh tabs to appear logged out.
  return window.localStorage;
}

function getLegacyStorage() {
  if (typeof window === "undefined") return null;
  // Backwards-compat: older versions stored auth in sessionStorage.
  return window.sessionStorage;
}

function safeGet(storage, key) {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(storage, key, value) {
  try {
    storage?.setItem(key, value);
  } catch {
    // ignore
  }
}

function safeRemove(storage, key) {
  try {
    storage?.removeItem(key);
  } catch {
    // ignore
  }
}

function maybeJsonUnwrap(raw) {
  if (raw == null) return null;

  // Many keys historically stored via JSON.stringify(value)
  try {
    const parsed = JSON.parse(raw);
    if (parsed == null) return null;

    const parsedType = typeof parsed;
    if (parsedType === "string" || parsedType === "number" || parsedType === "boolean") {
      return String(parsed);
    }
  } catch {
    // ignore
  }

  return raw;
}

export function getAuthString(key) {
  if (typeof window === "undefined") return null;

  if (WEB_TOKEN_KEYS.has(key) && authMemoryCache.has(key)) {
    return authMemoryCache.get(key) ?? null;
  }

  const primary = getPrimaryStorage();
  const legacy = getLegacyStorage();

  const rawPrimary = safeGet(primary, key);
  if (rawPrimary != null) {
    const value = maybeJsonUnwrap(rawPrimary);
    if (WEB_TOKEN_KEYS.has(key)) {
      authMemoryCache.set(key, value);
      safeRemove(primary, key);
      safeRemove(legacy, key);
    }
    return value;
  }

  const rawLegacy = safeGet(legacy, key);
  const value = maybeJsonUnwrap(rawLegacy);
  if (value != null && WEB_TOKEN_KEYS.has(key)) {
    authMemoryCache.set(key, value);
    safeRemove(primary, key);
    safeRemove(legacy, key);
  }
  return value;
}

export function setAuthString(key, value) {
  if (typeof window === "undefined") return;

  const primary = getPrimaryStorage();
  const legacy = getLegacyStorage();

  if (value == null || value === "") {
    authMemoryCache.delete(key);
    safeRemove(primary, key);
    safeRemove(legacy, key);
    emitAuthStorageChanged(key);
    return;
  }

  if (WEB_TOKEN_KEYS.has(key)) {
    // Do not persist embed tokens in Web Storage.
    authMemoryCache.set(key, String(value));
    safeRemove(primary, key);
    safeRemove(legacy, key);
    emitAuthStorageChanged(key);
    return;
  }

  safeSet(primary, key, String(value));
  // Migration: ensure it no longer remains in localStorage
  safeRemove(legacy, key);
  emitAuthStorageChanged(key);
}

export function getAuthJson(key) {
  if (typeof window === "undefined") return null;

  const primary = getPrimaryStorage();
  const legacy = getLegacyStorage();

  const rawPrimary = safeGet(primary, key);
  if (rawPrimary != null) {
    try {
      return JSON.parse(rawPrimary);
    } catch {
      return null;
    }
  }

  const rawLegacy = safeGet(legacy, key);
  if (rawLegacy != null) {
    try {
      return JSON.parse(rawLegacy);
    } catch {
      return null;
    }
  }

  return null;
}

export function setAuthJson(key, value) {
  if (typeof window === "undefined") return;

  const primary = getPrimaryStorage();
  const legacy = getLegacyStorage();

  if (value == null) {
    safeRemove(primary, key);
    safeRemove(legacy, key);
    return;
  }

  safeSet(primary, key, JSON.stringify(value));
  safeRemove(legacy, key);
}

export function clearAuthStorage() {
  if (typeof window === "undefined") return;

  authMemoryCache.clear();

  for (const key of AUTH_STORAGE_KEYS) {
    safeRemove(getPrimaryStorage(), key);
    safeRemove(getLegacyStorage(), key);
  }

  emitAuthStorageChanged("*");
}

export async function syncAuthFromOtherTabs(options) {
  if (typeof window === "undefined") return false;

  const timeoutMs =
    typeof options?.timeoutMs === "number" ? options.timeoutMs : 300;
  const keys = Array.isArray(options?.keys) ? options.keys : AUTH_SYNC_KEYS;

  const channel = getAuthSyncChannel();
  if (!channel) return false;

  const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const primary = getPrimaryStorage();

  return await new Promise((resolve) => {
    let resolved = false;
    const finish = (value) => {
      if (resolved) return;
      resolved = true;
      try {
        channel.close();
      } catch {
        // ignore
      }
      resolve(value);
    };

    const timer = setTimeout(() => finish(false), timeoutMs);

    channel.onmessage = (event) => {
      const msg = event?.data;
      if (!msg || msg.type !== "AUTH_SYNC_RESPONSE") return;
      if (msg.requestId !== requestId) return;

      const items = msg.items && typeof msg.items === "object" ? msg.items : {};
      let wroteAny = false;

      for (const [key, rawValue] of Object.entries(items)) {
        if (!key || rawValue == null) continue;
        if (WEB_TOKEN_KEYS.has(key)) continue;
        safeSet(primary, key, String(rawValue));
        emitAuthStorageChanged(key);
        wroteAny = true;
      }

      clearTimeout(timer);
      finish(wroteAny);
    };

    try {
      channel.postMessage({
        type: "AUTH_SYNC_REQUEST",
        requestId,
        keys,
      });
    } catch {
      clearTimeout(timer);
      finish(false);
    }
  });
}

export function getStoredUser() {
  return getAuthJson("user");
}

export function setStoredUser(user) {
  if (typeof window === "undefined") return;
  if (!user) return;

  setAuthJson("user", user);
}
